import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isMfaChallenge,
  LoginResponse,
  MfaChallengeResponse,
  useLogin,
  useLoginTwoFactor,
  useResendLoginCode,
} from "@/services/mutations/auth";
import { Link, useNavigate } from "react-router-dom";
import { Envelope } from "@phosphor-icons/react";
import { Hand } from "@/components/icons";
import {
  applyActiveOrganizationToUser,
  clearActiveOrganizationId,
  clearTokens,
  decodeJWT,
  setActiveOrganizationId,
} from "@/lib/api";
import useAuth from "@/store/use-auth";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";
import { FormEvent, useEffect, useState } from "react";
import { getMyOrganizations } from "@/services/queries/auth";
import { LoginOrganization } from "@/lib/types";

export interface LoginForm {
  username: string;
  password: string;
}

interface PendingLoginSession {
  organizations: LoginOrganization[];
  user: ReturnType<typeof decodeJWT>;
}

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [pendingLogin, setPendingLogin] = useState<PendingLoginSession | null>(
    null
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [isSubmittingOrganization, setIsSubmittingOrganization] =
    useState(false);
  const [challenge, setChallenge] = useState<MfaChallengeResponse | null>(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [resendsLeft, setResendsLeft] = useState(true);
  const [codeResent, setCodeResent] = useState(false);
  const isEmailMethod = challenge?.method === "email";
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  // Re-reads the clock every second against a fixed deadline, so throttled timers can't stretch the cooldown.
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(timer);
  }, [resendIn, now]);

  const startResendCooldown = (seconds: number) => {
    const start = Date.now();
    setNow(start);
    setResendAt(start + seconds * 1000);
  };

  const loginSchema = z.object({
    username: z
      .string()
      .email({ message: t("error_message.invalid_email") })
      .min(2, {
        message: t("error_message.minimum_characters", { count: 2 }),
      }),
    password: z.string().min(8, {
      message: t("error_message.minimum_characters", { count: 8 }),
    }),
  });

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const completeTokenLogin = async (data: LoginResponse) => {
    const userInfo = decodeJWT(data.accessToken);
    if (!userInfo) {
      return;
    }

    try {
      localStorage.setItem("access_token", data.accessToken);
      localStorage.setItem("refresh_token", data.refreshToken);
      localStorage.setItem(
        "token_expiry",
        ((Date.now() + data.expiresIn * 1000) / 1000).toString()
      );
    } catch (error) {
      console.error("Error storing access token in localStorage:", error);
    }

    clearActiveOrganizationId();

    try {
      const organizations = await getMyOrganizations();
      const availableOrganizations =
        organizations.length > 0
          ? organizations
          : [
              {
                organizationId: userInfo.org_id,
                organizationName: userInfo.org_id,
                roleId: "",
                roleName: userInfo.role,
              },
            ];

      if (availableOrganizations.length === 1) {
        const organizationId = availableOrganizations[0].organizationId;
        setActiveOrganizationId(organizationId);
        const activeUser = applyActiveOrganizationToUser(
          userInfo,
          organizationId
        );
        setUser(activeUser);
        Mixpanel.track("Login Successfully");
        navigate("/");
        return;
      }

      setPendingLogin({
        organizations: availableOrganizations,
        user: userInfo,
      });
      setSelectedOrganizationId(availableOrganizations[0].organizationId);
    } catch (error) {
      await clearTokens();
      setError(t("error_message.organization_loading_failed"));
    }
  };

  const { mutate: login, isPending } = useLogin({
    onSuccess: async ({ data }) => {
      if (isMfaChallenge(data)) {
        setChallenge(data);
        startResendCooldown(data.resendAvailableInSeconds ?? 0);
        // email_limit: a resend would hit the same hourly cap, so only a recovery code helps.
        setResendsLeft(data.emailError !== "email_limit");
        setCodeResent(false);
        if (data.emailError) setError(t(`error_message.${data.emailError}`));
        return;
      }

      await completeTokenLogin(data);
    },
    onError: (err) => {
      setError(
        err.response?.status === 429
          ? t("error_message.too_many_attempts")
          : t("error_message.invalid_username_or_password")
      );
      Mixpanel.track("Login Failed");
    },
  });

  const leaveTwoFactorStep = () => {
    setChallenge(null);
    setUseRecoveryCode(false);
    setTwoFactorCode("");
  };

  const { mutate: verifyTwoFactor, isPending: isVerifyingTwoFactor } =
    useLoginTwoFactor({
      onSuccess: async ({ data }) => {
        leaveTwoFactorStep();
        await completeTokenLogin(data);
      },
      onError: (err) => {
        if (err.response?.data?.error === "challenge_expired") {
          leaveTwoFactorStep();
          setError(t("error_message.two_factor_expired"));
        } else if (err.response?.status === 429) {
          setError(t("error_message.too_many_attempts"));
        } else {
          setError(
            useRecoveryCode
              ? t("error_message.invalid_recovery_code")
              : isEmailMethod
                ? t("error_message.invalid_email_code")
                : t("error_message.invalid_two_factor_code")
          );
        }
      },
    });

  const { mutate: resendCode, isPending: isResending } = useResendLoginCode({
    onSuccess: ({ data }) => {
      setError(null);
      setCodeResent(true);
      setChallenge((current) => current && { ...current, emailError: null });
      startResendCooldown(data.resendAvailableInSeconds);
      setResendsLeft(data.resendsRemaining > 0);
    },
    onError: (err) => {
      setCodeResent(false);
      switch (err.response?.data?.error) {
        case "challenge_expired":
          leaveTwoFactorStep();
          setError(t("error_message.two_factor_expired"));
          return;
        case "resend_cooldown":
          startResendCooldown(err.response.data.retryAfterSeconds ?? 60);
          return;
        case "resend_limit":
          setResendsLeft(false);
          setError(t("error_message.resend_limit"));
          return;
        case "email_limit":
          setResendsLeft(false);
          setError(t("error_message.email_limit"));
          return;
        case "email_send_failed":
          // Not counted by the server and the previous code still works, so resend stays enabled.
          setError(t("error_message.email_send_failed"));
          return;
        default:
          setError(
            err.response?.status === 429
              ? t("error_message.too_many_attempts")
              : t("error_message.something_went_wrong")
          );
      }
    },
  });

  const onSubmitTwoFactor = (event: FormEvent) => {
    event.preventDefault();
    if (!challenge) return;

    const value = twoFactorCode.trim();
    if (!value) {
      setError(
        useRecoveryCode
          ? t("error_message.recovery_code_required")
          : isEmailMethod
            ? t("error_message.email_code_required")
            : t("error_message.two_factor_code_required")
      );
      return;
    }

    setError(null);
    verifyTwoFactor(
      useRecoveryCode
        ? { challengeId: challenge.challengeId, recoveryCode: value }
        : { challengeId: challenge.challengeId, code: value }
    );
  };

  function onSubmit(values: LoginForm) {
    setError(null);
    setPendingLogin(null);
    setSelectedOrganizationId("");
    login(values);
  }

  const completeLogin = () => {
    if (!pendingLogin || !pendingLogin.user) {
      return;
    }

    if (!selectedOrganizationId) {
      setError(t("error_message.organization_selection_required"));
      return;
    }

    setIsSubmittingOrganization(true);

    try {
      setActiveOrganizationId(selectedOrganizationId);
      const activeUser = applyActiveOrganizationToUser(
        pendingLogin.user,
        selectedOrganizationId
      );
      setUser(activeUser);
      Mixpanel.track("Login Successfully");
      navigate("/");
    } finally {
      setIsSubmittingOrganization(false);
    }
  };

  const pageTitle = challenge
    ? t("title.two_factor")
    : pendingLogin
      ? t("title.select_organization")
      : t("title.login");

  return (
    <>
      <title>{pageTitle}</title>

      <div className="flex h-screen items-center justify-center bg-[url('/images/auth-bg.png')] bg-cover bg-center bg-no-repeat">
        <div className="flex w-full max-w-md flex-col items-center justify-center p-5 text-3xl font-bold">
          <img src="/svgs/logo.png" alt="logo" className="h-[45px] w-[45px]" />
          <span className="mb-8 mt-2 text-[26px] font-normal leading-8 text-white">
            {pageTitle}
          </span>

          {challenge ? (
            <form
              onSubmit={onSubmitTwoFactor}
              className="w-full space-y-3"
              data-testid="login-two-factor-form"
            >
              <p className="text-center text-sm font-normal leading-5 text-[#C6D5E9]">
                {useRecoveryCode
                  ? t("description.two_factor_recovery")
                  : isEmailMethod
                    ? t(
                        challenge.emailError
                          ? "description.two_factor_email_not_sent"
                          : "description.two_factor_email",
                        { email: challenge.emailHint ?? "" }
                      )
                    : t("description.two_factor_code")}
              </p>
              <Input
                id="two-factor-code"
                aria-label={
                  useRecoveryCode
                    ? t("label.recovery_code")
                    : t("label.authentication_code")
                }
                placeholder={
                  useRecoveryCode
                    ? t("label.recovery_code")
                    : t("label.authentication_code")
                }
                value={twoFactorCode}
                onChange={(e) => {
                  setTwoFactorCode(e.target.value);
                  setError(null);
                }}
                autoComplete="one-time-code"
                inputMode={useRecoveryCode ? "text" : "numeric"}
                maxLength={useRecoveryCode ? 11 : 6}
                autoFocus
              />
              <Button
                isPending={isVerifyingTwoFactor}
                disabled={isVerifyingTwoFactor}
                className="w-full"
                type="submit"
              >
                {t("button.verify")}
              </Button>
              {isEmailMethod && !useRecoveryCode && resendsLeft && (
                <Button
                  variant="link"
                  className="w-full text-[#C6D5E9]"
                  type="button"
                  disabled={resendIn > 0 || isResending}
                  onClick={() => {
                    // Empty the live region first so an identical confirmation is announced again.
                    setCodeResent(false);
                    resendCode(challenge.challengeId);
                  }}
                  data-testid="login-two-factor-resend-button"
                >
                  {resendIn > 0
                    ? t("button.resend_code_in", { seconds: resendIn })
                    : t("button.resend_code")}
                </Button>
              )}
              {/* Always mounted so screen readers pick up changes to it. */}
              <p
                role="status"
                className="text-center text-sm font-normal leading-5 text-[#C6D5E9] empty:!mt-0"
              >
                {isEmailMethod && !useRecoveryCode && codeResent && (
                  <>
                    {t("description.two_factor_resent")}
                    {!resendsLeft &&
                      ` ${t("description.two_factor_no_more_codes")}`}
                  </>
                )}
              </p>
              <Button
                variant="link"
                className="w-full text-[#C6D5E9]"
                type="button"
                onClick={() => {
                  setUseRecoveryCode(!useRecoveryCode);
                  setTwoFactorCode("");
                  setError(null);
                }}
              >
                {useRecoveryCode
                  ? isEmailMethod
                    ? t("button.use_email_code")
                    : t("button.use_authenticator_code")
                  : t("button.use_recovery_code")}
              </Button>
              <Button
                variant="link"
                className="w-full text-[#C6D5E9]"
                type="button"
                onClick={() => {
                  leaveTwoFactorStep();
                  setError(null);
                }}
              >
                {t("button.back")}
              </Button>
            </form>
          ) : pendingLogin ? (
            <div className="w-full space-y-3">
              <p className="text-center text-sm font-normal leading-5 text-[#C6D5E9]">
                {t("description.select_organization")}
              </p>
              <Select
                value={selectedOrganizationId}
                onValueChange={(value) => {
                  setError(null);
                  setSelectedOrganizationId(value);
                }}
              >
                <SelectTrigger data-testid="organization-select-trigger">
                  <SelectValue placeholder={t("label.organization")} />
                </SelectTrigger>
                <SelectContent>
                  {pendingLogin.organizations.map((organization) => (
                    <SelectItem
                      key={organization.organizationId}
                      value={organization.organizationId}
                    >
                      {organization.organizationName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                isPending={isSubmittingOrganization}
                disabled={isSubmittingOrganization}
                className="w-full"
                type="button"
                onClick={completeLogin}
              >
                {t("button.confirm")}
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-full space-y-3"
              >
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          invalid={!!form.formState.errors.username}
                          id="email"
                          placeholder={t("label.email")}
                          {...field}
                          icon={Envelope}
                        />
                      </FormControl>
                      <FormMessage data-testid="email-error" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          invalid={!!form.formState.errors.password}
                          autoComplete="off"
                          id="password"
                          placeholder={t("label.password")}
                          type="password"
                          showHidePassword={true}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-testid="password-error" />
                    </FormItem>
                  )}
                />
                <Button
                  isPending={isPending}
                  disabled={isPending}
                  className="w-full"
                  type="submit"
                >
                  {t("button.login")}
                </Button>
              </form>
            </Form>
          )}
          {error && (
            <p
              role="alert"
              data-testid="login-error"
              className="mx-auto !mt-4 text-center text-[13px] font-medium leading-[17px] text-[#E86363]"
            >
              {error}
            </p>
          )}

          <Link
            data-testid="forgot-password-link"
            to="/forgot-password"
            className="group mt-14 flex items-center justify-center gap-3"
          >
            <Hand size={31} />
            <span className="text-sm font-medium leading-4 text-[#C6D5E9] transition-colors duration-200 group-hover:text-[#49CEE6]/80">
              {t("label.forget_password")}
            </span>
          </Link>
        </div>
      </div>
    </>
  );
};

export default Login;
